import React, { createContext, useContext } from "react";
import MuiDialog from "@material-ui/core/Dialog";
import { makeStyles } from "@material-ui/core/styles";

const DialogContext = createContext();

export const useDialogContext = () => {
  const context = useContext(DialogContext);
  if (!context) {
    throw new Error("Dialog sub-components must be used within <Dialog>");
  }
  return context;
};

const useStyles = makeStyles(() => ({
  dialog: {
    "& .MuiDialog-paper": {
      borderRadius: "8px",
      overflow: "hidden",
    },
  },
}));

/**
 * Root Dialog wrapper built on top of MUI Dialog.
 * Provides consistent border-radius and passes context to child components.
 *
 * @param {boolean}  open       - Whether the dialog is open
 * @param {function} onClose    - Handler called when the dialog should close
 * @param {string}   maxWidth   - MUI maxWidth ("xs" | "sm" | "md" | "lg" | "xl") — default "sm"
 * @param {boolean}  fullWidth  - Whether the dialog stretches to maxWidth — default true
 * @param {string}   className  - Additional class name
 * @param {object}   style      - Additional inline styles on the MUI Dialog paper
 * @param {object}   rest       - Any other MUI Dialog props (disableBackdropClick, disableEscapeKeyDown, onEntered, …)
 */
const Dialog = ({
  open,
  onClose,
  maxWidth = "sm",
  fullWidth = true,
  className = "",
  style = {},
  children,
  ...rest
}) => {
  const classes = useStyles();

  return (
    <DialogContext.Provider value={{ onClose }}>
      <MuiDialog
        open={open}
        onClose={onClose}
        maxWidth={maxWidth}
        fullWidth={fullWidth}
        className={`${classes.dialog} ${className}`}
        PaperProps={{ style }}
        {...rest}
      >
        {children}
      </MuiDialog>
    </DialogContext.Provider>
  );
};

export default Dialog;
